/**
 * Track 2: pure mapping from game-state changes to audio cues (unit-tested, no Web Audio here).
 */
import type { FleetClient, ConflictReport } from '@galaxis/shared';
import type { SfxName } from './engine.ts';
import { pickMood, type Mood } from './settings.ts';

/** Which sounds a fleet-list update should trigger: depart / arrive (status edge) and complete (new ship). */
export function fleetCues(prev: FleetClient[], next: FleetClient[]): SfxName[] {
  if (prev.length === 0) return []; // first load is not news
  const before = new Map(prev.map(f => [f.id, f]));
  const cues: SfxName[] = [];
  let shipsBefore = 0, shipsAfter = 0;
  for (const f of prev) shipsBefore += f.ships.length;
  for (const f of next) {
    shipsAfter += f.ships.length;
    const p = before.get(f.id);
    if (!p) continue;
    if (p.status !== 'traveling' && f.status === 'traveling') cues.push('depart');
    else if (p.status === 'traveling' && f.status !== 'traveling') cues.push('arrive');
  }
  if (shipsAfter > shipsBefore) cues.push('complete');
  return cues.slice(0, 3);
}

/** Mood from client state: a live battle, or one of our systems / fleets in an active conflict, raises the music. */
export function moodFor(state: { liveBattle: unknown; conflicts: ConflictReport[]; myCivId: string | undefined }): Mood {
  const hostileNear = !!state.myCivId && state.conflicts.some(c =>
    (c.status === 'battle_in_progress' || c.status === 'occupied')
    && (c.attackerCivId === state.myCivId || c.defenderCivId === state.myCivId));
  return pickMood({ inBattle: !!state.liveBattle, hostileNear });
}

/** Elements whose click should make the UI "tick". */
export function isClickable(el: Element | null): boolean {
  const t = el?.closest?.('button, a[href], [role="button"], [role="tab"], summary, input[type="checkbox"], input[type="radio"]');
  return !!t && !(t as HTMLButtonElement).disabled && t.getAttribute('aria-disabled') !== 'true' && t.getAttribute('data-silent') === null;
}
